import { useEffect, useState } from 'react';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { setNewDevice, putExistingDevice } from 'src/api/devices';
import { getTimezoneList } from 'src/api/analytics';
import { getServers } from 'src/api/servers';
import { jsonToFormData } from 'src/utils/config';
import cameraModelComboClientConfig from 'src/config/cameraModelComboClientConfig.json';

// ----------------------------------------------------------------------

// values the client never picks, they are only sent along with the payload
const CAMERA_DEFAULTS = {
  Footage: 1, // video
  IsAgentBased: 0, // event send to = client
  Category: '2', // the api rejects a numeric category with a 422
  IsActive: 0, // is need archives -- off, a client camera is not archived unless asked for
  BackupDurationDays: 15, // ignored while IsActive is 0, still sent so the api has a value
  EventsBackupDays: 15,
  EscalatedEventsBackupDays: 90,
  BeforeVideo: 5,
  AfterVideo: 25,
};

// Servers a client may pick from. Only the ids listed against that client in
// cameraModelComboClientConfig.json are offered, so no other client's server
// can ever be selected here.
const getAllowedServerIds = (clientId) => {
  const config = cameraModelComboClientConfig.find(
    (entry) => Number(entry.client_id) === Number(clientId)
  );
  return (config?.camera_server_ids || []).map(Number).filter((id) => !Number.isNaN(id));
};

// Required fields carry a red asterisk, the rest say "(optional)" outright, so
// nothing is left to be inferred from the absence of a mark.
const requiredSx = { '& .MuiFormLabel-asterisk': { color: 'error.main' } };

const optionalLabel = (text) => (
  <>
    {text}{' '}
    <Typography component="span" variant="caption" sx={{ color: 'text.secondary' }}>
      (optional)
    </Typography>
  </>
);

// the only fields the client fills in
const EMPTY_FORM = {
  devicename: '',
  substream: '',
  ReStream: '',
  server_id: '',
  TimezoneId: '',
  fps: '',
};

const toFormState = (device) => {
  if (!device) return { ...EMPTY_FORM };
  return Object.keys(EMPTY_FORM).reduce((acc, key) => {
    // fps 0 means "not set" on the api side, so show the field blank rather
    // than a 0 the user has to clear. normalize() sends it back as 0.
    const isUnsetFps = key === 'fps' && Number(device[key]) === 0;
    acc[key] = device[key] === null || device[key] === undefined || isUnsetFps ? '' : device[key];
    return acc;
  }, {});
};

// ----------------------------------------------------------------------

export default function ClientCameraForm({ open, device, clientId, onClose, onSaved }) {
  const isEdit = Boolean(device);

  const [values, setValues] = useState({ ...EMPTY_FORM });
  const [errors, setErrors] = useState({});
  const [timezones, setTimezones] = useState([]);
  const [servers, setServers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [apiError, setApiError] = useState('');

  useEffect(() => {
    getTimezoneList((response) => {
      if (response.status === 200) setTimezones(response.data || []);
    });
  }, []);

  useEffect(() => {
    getServers((response) => {
      if (response.status === 200) setServers(Array.isArray(response.data) ? response.data : []);
    });
  }, []);

  // the servers this client is allowed to pick. an existing camera already sitting
  // on some other server keeps that server in the list, so editing it never has to
  // silently move the camera somewhere else.
  const allowedServerIds = getAllowedServerIds(clientId);
  const serverOptions = servers.filter((server) => (
    allowedServerIds.includes(Number(server.Id)) || Number(server.Id) === Number(device?.server_id)
  ));

  useEffect(() => {
    if (!open) return;
    setValues(toFormState(device));
    setErrors({});
    setApiError('');
  }, [open, device]);

  // a client with a single configured server never has a choice to make, so it is
  // filled in for a new camera the way it used to be hard coded
  useEffect(() => {
    if (!open || isEdit) return;
    if (serverOptions.length !== 1) return;
    setValues((prev) => (prev.server_id ? prev : { ...prev, server_id: serverOptions[0].Id }));
  }, [open, isEdit, serverOptions]);

  // a new camera starts on the logged in client's own timezone -- the same
  // timezone_id the rest of the app reads off the login -- so the common case
  // needs no pick. an unknown id is left alone rather than forced into the field.
  useEffect(() => {
    if (!open || isEdit) return;
    const clientTimezoneId = Number(localStorage.getItem('timezone_id'));
    if (!clientTimezoneId) return;
    if (!timezones.some((timezone) => Number(timezone.Id) === clientTimezoneId)) return;
    setValues((prev) => (prev.TimezoneId ? prev : { ...prev, TimezoneId: clientTimezoneId }));
  }, [open, isEdit, timezones]);

  const handleChange = (field) => (event) => {
    const { value } = event.target;
    setValues((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const validate = () => {
    const nextErrors = {};
    if (!String(values.devicename).trim()) nextErrors.devicename = 'Camera Name is required';
    if (!String(values.substream).trim()) nextErrors.substream = 'RTSP URL is required';
    if (!String(values.server_id)) nextErrors.server_id = 'Server is required';
    if (!String(values.TimezoneId)) nextErrors.TimezoneId = 'Timezone is required';
    if (String(values.fps).trim() && Number.isNaN(Number(values.fps))) {
      nextErrors.fps = 'FPS must be a number';
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const normalize = (form) => ({
    devicename: String(form.devicename).trim(),
    substream: String(form.substream).trim(),
    ReStream: String(form.ReStream).trim(),
    server_id: Number(form.server_id),
    TimezoneId: Number(form.TimezoneId),
    fps: String(form.fps).trim() === '' ? 0 : parseInt(form.fps, 10),
  });

  const handleSubmit = () => {
    if (!validate()) return;

    setSaving(true);
    setApiError('');
    const payload = normalize(values);

    const done = (response) => {
      setSaving(false);
      if (response.status === 200 || response.status === 201) {
        if (onSaved) onSaved();
        onClose();
      } else {
        setApiError('Could not save the camera. Please try again.');
      }
    };

    if (isEdit) {
      // send only what actually changed so nothing else on the camera gets overwritten
      const original = normalize(toFormState(device));
      const changed = Object.keys(payload).reduce((acc, key) => {
        if (payload[key] !== original[key]) acc[key] = payload[key];
        return acc;
      }, {});

      if (Object.keys(changed).length === 0) {
        setSaving(false);
        onClose();
        return;
      }

      changed.deviceid = device.deviceid;
      changed.clientid = device.clientid;
      putExistingDevice(done, jsonToFormData(changed));
    } else {
      setNewDevice(done, [{ ...CAMERA_DEFAULTS, ...payload, clientid: String(clientId) }]);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>{isEdit ? 'Edit Camera' : 'Add Camera'}</DialogTitle>

      <DialogContent dividers>
        {apiError && <Alert severity="error" sx={{ mb: 2 }}>{apiError}</Alert>}

        <Grid container spacing={2} sx={{ pt: 1 }}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              required
              sx={requiredSx}
              label="Camera Name"
              value={values.devicename}
              onChange={handleChange('devicename')}
              error={!!errors.devicename}
              helperText={errors.devicename || ''}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              required
              sx={requiredSx}
              label="RTSP URL"
              value={values.substream}
              onChange={handleChange('substream')}
              error={!!errors.substream}
              helperText={errors.substream || ''}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label={optionalLabel('Re Stream')}
              value={values.ReStream}
              onChange={handleChange('ReStream')}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              select
              fullWidth
              required
              sx={requiredSx}
              label="Server"
              value={serverOptions.length ? values.server_id : ''}
              onChange={handleChange('server_id')}
              error={!!errors.server_id}
              helperText={errors.server_id || ''}
            >
              {serverOptions.map((server) => (
                <MenuItem key={server.Id} value={server.Id}>{server.ServerName}</MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              select
              fullWidth
              required
              sx={requiredSx}
              label="Timezone"
              value={values.TimezoneId}
              onChange={handleChange('TimezoneId')}
              error={!!errors.TimezoneId}
              helperText={errors.TimezoneId || ''}
            >
              {timezones.map((timezone) => (
                <MenuItem key={timezone.Id} value={timezone.Id}>{timezone.TimeZone}</MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={optionalLabel('FPS')}
              value={values.fps}
              onChange={handleChange('fps')}
              error={!!errors.fps}
              helperText={errors.fps || ''}
              inputProps={{ inputMode: 'numeric' }}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <LoadingButton variant="contained" loading={saving} onClick={handleSubmit}>
          {isEdit ? 'Update' : 'Save'}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
